import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AudioStreamService } from '../audio-stream/audio-stream.service';
import { StorageService } from '../../storage-service/storage.service';
import { CVANotificationService } from '../../cva-notification/cva-notification.service';
import { NotificationActivityService } from '../../notification-activity/notification-activity.service';
import { NotificationClassEnum } from '../../../models/notification-class.enum';
import { NotificationTypeEnum } from '../../../models/notification-activity.model';
import { MESSAGES, StorageKeys } from '../../../util/constants';
import { TelehealthGuideDialogComponent } from '../../../components/telehealth-guide-dialog/telehealth-guide-dialog.component';

@Injectable({ providedIn: 'root' })
export class TelehealthSessionService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly audioStreamService: AudioStreamService,
    private readonly storageService: StorageService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly notificationActivityService: NotificationActivityService,
  ) { }

  get isSupportedBrowser(): boolean {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);
    return isChrome || isEdge;
  }

  async prepare(): Promise<boolean> {
    if (!this.isSupportedBrowser) {
      this.cvaNotificationService.showNotification(
        MESSAGES.TELEHEALTH_UNSUPPORTED,
        NotificationClassEnum.ERROR,
      );
      return false;
    }

    if (!(await this.isTourTaken())) {
      const continued = await this.showGuide();
      if (!continued) return false;
    }

    try {
      const stream = await this.audioStreamService.initializeTelehealthMode();
      return !!stream && this.audioStreamService.hasActiveTelehealthAudio;
    } catch (error) {
      this.cvaNotificationService.showNotification(
        error instanceof Error ? error.message : MESSAGES.TELEHEALTH_NOT_READY,
        NotificationClassEnum.ERROR,
      );
      return false;
    }
  }

  private async isTourTaken(): Promise<boolean> {
    if (this.storageService.getItem<boolean>(StorageKeys.TELEHEALTH_TOUR_TAKEN) === true) {
      return true;
    }

    try {
      const result = await firstValueFrom(
        this.notificationActivityService.checkTourStatus(NotificationTypeEnum.TELEHEALTH_TOUR),
      );
      const taken = !!result?.response?.isTourTaken;
      if (taken) {
        this.storageService.setItem(StorageKeys.TELEHEALTH_TOUR_TAKEN, true);
      }
      return taken;
    } catch {
      return false;
    }
  }

  private markTourTaken(): void {
    this.storageService.setItem(StorageKeys.TELEHEALTH_TOUR_TAKEN, true);
    this.notificationActivityService
      .setInitialTourTakenStatus(NotificationTypeEnum.TELEHEALTH_TOUR)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private async showGuide(): Promise<boolean> {
    const dialogRef = this.dialog.open(TelehealthGuideDialogComponent, {
      width: '540px',
      maxWidth: 'calc(100vw - 32px)',
      disableClose: true,
      autoFocus: false,
      panelClass: 'telehealth-guide-dialog-panel',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result?.continue) return false;
    if (result.doNotShowAgain) this.markTourTaken();
    return true;
  }
}
